import type { TicketGroup } from '@interfaces/ticketevolution/tikets';
import type { ZoneBucket } from '@v2/views/SearchTickets/utils/ticket-ranking';
import { create } from 'zustand';

export type TicketSortBy = 'lowestPrice' | 'bestValue' | 'bestLocation';
export type TicketPriceRange = [number, number];

type TicketsStore = {
  selectedGroups: string[];
  ticketsQuantity: number;
  isOnlyElectronic: boolean;
  accessibleOnly: boolean;
  zoneBuckets: ZoneBucket[];
  underPriceCap: number | null;
  priceRange: TicketPriceRange;
  ticketSelected: TicketGroup | null;
  sortby: TicketSortBy;
  taxQuota: TaxQuota;
  isTicketSelectionConfirmed: boolean;
  hideMapPrices: boolean;
};

type TaxQuota = {
  tax: number;
  tax_signature: string | null;
};

type Actions = {
  setSelectedGroups: (val: string[]) => void;
  setTickets: (val: 'increment' | 'decrement') => void;
  setPriceRange: (val: number[]) => void;
  setSortBy: (val: TicketSortBy) => void;
  setZoneBuckets: (val: ZoneBucket[]) => void;
  toggleZoneBucket: (val: ZoneBucket) => void;
  setUnderPriceCap: (val: number | null) => void;
  resetFilters: () => void;
  reset: () => void;
  setTicketSelectionConfirmed: ({
    isTicketSelectionConfirmed,
    taxQuota,
  }: {
    taxQuota: TaxQuota;
    isTicketSelectionConfirmed: boolean;
  }) => void;
  toggleBoolean: (
    val: keyof Pick<
      TicketsStore,
      'isOnlyElectronic' | 'accessibleOnly' | 'hideMapPrices'
    >
  ) => void;
  setTicketSelected: (val: TicketGroup | null) => void;
};

export const INITIAL_TICKET_PRICE_RANGE: TicketPriceRange = [0, 1000];

const initState: TicketsStore = {
  selectedGroups: [],
  ticketsQuantity: 2,
  sortby: 'lowestPrice',
  isOnlyElectronic: false,
  accessibleOnly: false,
  zoneBuckets: [],
  underPriceCap: null,
  priceRange: INITIAL_TICKET_PRICE_RANGE,
  ticketSelected: null,
  taxQuota: {
    tax: 0,
    tax_signature: null,
  },
  isTicketSelectionConfirmed: false,
  hideMapPrices: false,
};

export const useTicketsStore = create<TicketsStore & Actions>((set) => ({
  ...initState,
  setPriceRange: (val) =>
    set({ priceRange: [val[0] ?? 0, val[1] ?? val[0] ?? 0] }),
  setSortBy: (val) => set({ sortby: val }),
  setZoneBuckets: (zoneBuckets) => set({ zoneBuckets }),
  toggleZoneBucket: (bucket) =>
    set((state) => {
      const exists = state.zoneBuckets.includes(bucket);
      return {
        zoneBuckets: exists
          ? state.zoneBuckets.filter((z) => z !== bucket)
          : [...state.zoneBuckets, bucket],
      };
    }),
  setUnderPriceCap: (underPriceCap) => set({ underPriceCap }),
  setTicketSelectionConfirmed: (val) => set({ ...val }),
  setSelectedGroups: (selectedGroups) => set({ selectedGroups }),
  setTicketSelected: (ticketSelected) =>
    set({
      ticketSelected,
      isTicketSelectionConfirmed: false,
      taxQuota: { tax: 0, tax_signature: null },
    }),
  toggleBoolean: (key) => set((state) => ({ [key]: !state[key] })),
  setTickets: (val) => {
    set((state) => {
      if (val === 'increment') {
        return { ticketsQuantity: state.ticketsQuantity + 1 };
      }
      if (val === 'decrement' && state.ticketsQuantity > 1) {
        return { ticketsQuantity: state.ticketsQuantity - 1 };
      }
      return state;
    });
  },
  resetFilters: () =>
    set({
      isOnlyElectronic: false,
      accessibleOnly: false,
      zoneBuckets: [],
      underPriceCap: null,
      selectedGroups: [],
      sortby: 'lowestPrice',
    }),
  reset: () => {
    set(initState);
  },
}));
